import React, { useState, useEffect, useCallback } from 'react';
import { investigationService } from '../services/api';
import '../styles/CollaboratorsPanel.css';

export default function CollaboratorsPanel({ investigationId }) {
  const [collaborators, setCollaborators] = useState([]);
  const [loading, setLoading]             = useState(true);
  const [username, setUsername]           = useState('');
  const [role, setRole]                   = useState('viewer');
  const [submitting, setSubmitting]       = useState(false);
  const [error, setError]                 = useState(null);

  const loadCollaborators = useCallback(async () => {
    try {
      setLoading(true);
      const response = await investigationService.getCollaborators(investigationId);
      setCollaborators(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Failed to load collaborators:', err);
    } finally {
      setLoading(false);
    }
  }, [investigationId]);

  useEffect(() => { loadCollaborators(); }, [loadCollaborators]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!username.trim()) return;
    try {
      setSubmitting(true);
      setError(null);
      await investigationService.addCollaborator(investigationId, username.trim(), role);
      setUsername('');
      setRole('viewer');
      loadCollaborators();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to add collaborator');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await investigationService.updateCollaborator(investigationId, userId, newRole);
      loadCollaborators();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleRemove = async (userId) => {
    if (!window.confirm('Remove this collaborator? They will lose access to this investigation.')) return;
    try {
      await investigationService.removeCollaborator(investigationId, userId);
      loadCollaborators();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to remove collaborator');
    }
  };

  return (
    <div className="collab-panel">
      <h3>👥 Share This Investigation</h3>
      <p className="collab-hint">
        Give another user access without making them the owner. <strong>Viewer</strong> can see
        the graph, notes, and tags. <strong>Editor</strong> can also fetch data, import
        transactions, and add notes/tags — but can't delete the investigation or manage sharing.
      </p>

      <form onSubmit={handleAdd} className="collab-add-form">
        <input
          type="text"
          placeholder="Username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          className="collab-username-input"
        />
        <select value={role} onChange={(e) => setRole(e.target.value)} className="collab-role-select">
          <option value="viewer">Viewer</option>
          <option value="editor">Editor</option>
        </select>
        <button type="submit" disabled={submitting || !username.trim()} className="btn-submit">
          {submitting ? 'Adding...' : 'Add'}
        </button>
      </form>

      {error && <div className="collab-error">⚠ {error}</div>}

      {loading ? (
        <p>Loading collaborators...</p>
      ) : collaborators.length === 0 ? (
        <p className="empty">Not shared with anyone yet.</p>
      ) : (
        <div className="collab-list">
          {collaborators.map((c) => (
            <div key={c.user_id} className="collab-item">
              <div className="collab-item-main">
                <strong>{c.username}</strong>
                <span className="collab-added">added {new Date(c.created_at).toLocaleDateString()}</span>
              </div>
              <div className="collab-item-actions">
                <select
                  value={c.role}
                  onChange={(e) => handleRoleChange(c.user_id, e.target.value)}
                  className="collab-role-select small"
                >
                  <option value="viewer">Viewer</option>
                  <option value="editor">Editor</option>
                </select>
                <button onClick={() => handleRemove(c.user_id)} className="btn-delete">
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
